import { useSyncExternalStore } from "react";

import { consumeInstallPrompt, getCapturedInstallPrompt, subscribeToInstallPrompt } from "./installPromptCapture";
import {
  dismissInstallCard,
  getAlreadyInstalledNoticeVisible,
  getCardDismissed,
  getIosInstructionsVisible,
  hideAlreadyInstalledNotice,
  hideIosInstallInstructions,
  isIosDevice,
  isStandaloneDisplay,
  showAlreadyInstalledNotice as markAlreadyInstalledNoticeVisible,
  showIosInstallInstructions,
  subscribeToAlreadyInstalledNoticeVisible,
  subscribeToCardDismissed,
  subscribeToIosInstructionsVisible,
} from "./pwaInstallVisibility";

// Single source of truth for PWA install availability, shared by the automatic install card
// (PwaStatusPrompts) and the persistent "Install MedoPilot" entry points in the marketing nav and
// footer, so install logic and capability detection are never duplicated between them.
export function usePwaInstallState() {
  const installPrompt = useSyncExternalStore(subscribeToInstallPrompt, getCapturedInstallPrompt);
  const cardDismissed = useSyncExternalStore(subscribeToCardDismissed, getCardDismissed);
  const showIosInstructions = useSyncExternalStore(subscribeToIosInstructionsVisible, getIosInstructionsVisible);
  const showAlreadyInstalledNotice = useSyncExternalStore(
    subscribeToAlreadyInstalledNoticeVisible,
    getAlreadyInstalledNoticeVisible,
  );
  const isStandalone = isStandaloneDisplay();
  const isIos = isIosDevice();

  const canPromptInstall = Boolean(installPrompt) && !isStandalone;
  const canShowIosGuidance = isIos && !installPrompt && !isStandalone;
  // Whether install is possible at all on this platform. Used by the explicit entry points, which
  // stay available regardless of the automatic card's own dismissal state -- the two are
  // intentionally independent (see cardDismissed vs. showIosInstructions above).
  const isInstallable = canPromptInstall || canShowIosGuidance;

  // Shared by both the automatic card's own "Install" button and the explicit nav/footer entry
  // points: prompt() only ever runs as a direct result of whichever one the user actually clicked.
  const requestInstall = async () => {
    if (!installPrompt) {
      return;
    }

    try {
      await installPrompt.prompt();
      await installPrompt.userChoice;
    } catch {
      // Ignore: the user dismissing the native prompt, or the browser rejecting a re-used
      // event, are both handled the same way below -- the event is consumed either way so it
      // is never reused (each BeforeInstallPromptEvent can only be prompted once).
    } finally {
      consumeInstallPrompt();
      dismissInstallCard();
    }
  };

  return {
    isStandalone,
    isIos,
    canPromptInstall,
    canShowIosGuidance,
    isInstallable,
    cardDismissed,
    showIosInstructions,
    showAlreadyInstalledNotice,
    requestInstall,
    dismiss: dismissInstallCard,
    openIosInstructions: showIosInstallInstructions,
    closeIosInstructions: hideIosInstallInstructions,
    openAlreadyInstalledNotice: markAlreadyInstalledNoticeVisible,
    closeAlreadyInstalledNotice: hideAlreadyInstalledNotice,
  };
}
